import React, { useCallback } from 'react';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  styled,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ProviderNoteComment } from '../../../data/models/provider-note.model';
import * as providerSlice from '../../../data/provider.slice';
import TextInput from '../../form/TextInput';

const PREFIX = 'NoteCommentDialog';

const classes = {
  form: `${PREFIX}-form`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.form}`]: {
    margin: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface NoteCommentDialogProps {
  providerId: number;
  noteId: number;
  title: string;
  comment: ProviderNoteComment | undefined;
  open: boolean;
  close: (saved: boolean) => void;
}

export default function NoteDialog({
  providerId,
  noteId,
  title,
  comment,
  open,
  close,
}: NoteCommentDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const saving = useAppSelector(providerSlice.selectSavingNote);
  const saved = useAppSelector(providerSlice.selectSavedNote);
  // const savingError = useAppSelector(providerSlice.selectSavingCommentError);

  const { control, formState, handleSubmit, reset } = useForm();

  React.useEffect(() => {
    reset({
      Note: comment?.note || '',
    });
  }, [comment, reset]);

  const closeDialog = useCallback(
    (saved: boolean) => {
      reset({ Note: '' });
      close(saved);
    },
    [close, reset],
  );

  React.useEffect(() => {
    if (!saving && saved) {
      dispatch(providerSlice.resetSavingNoteFlags());
      closeDialog(true);
    }
  }, [closeDialog, dispatch, saved, saving]);

  const saveComment = React.useCallback(
    (data: any) => {
      const fn = comment
        ? providerSlice.updateNoteCommentAsync(providerId, {
            ...comment,
            note: data.Note,
          })
        : providerSlice.createNoteCommentAsync(providerId, noteId, {
            transportationProviderID: providerId,
            note: data.Note,
          });
      dispatch(fn);
    },
    [dispatch, providerId, noteId, comment],
  );

  return (
    <StyledDialog
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth={true}
      open={open}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <div className={classes.form}>
          <TextInput
            label={t('provider.note')}
            name="Note"
            required={true}
            control={control}
            errors={formState.errors}
            rules={
              requiredFieldMessage ? { required: requiredFieldMessage } : {}
            }
            inputProps={{ maxLength: 500 }}
          />
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          data-testid="save"
          variant="contained"
          color="primary"
          onClick={handleSubmit(saveComment)}
          autoFocus>
          {t('common.save')}
        </Button>
        <Button
          data-testid="cancel"
          variant="contained"
          color="secondary"
          onClick={() => closeDialog(false)}>
          {t('common.cancel')}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
}
